import _ from 'lodash';
import moment from 'moment';
import { PropsWithChildren, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useFeature, useLocalize, useViewer } from '@treams/treams-hooks';
import {
	AddIcon,
	CalendarIcon,
	ChevronRightIcon,
	ConversationIcon,
	DialogueIcon,
	DraftsIcon,
	FriendsIcon,
	HeartIcon,
	LibraryIcon,
	SidebarRightCloseIcon,
	SuitcaseIcon,
	TargetIcon,
} from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Compliment } from '@treams/treams-network';
import {
	Avatar,
	Button,
	Card,
	CardList,
	FlexGap,
	Heading3,
	IconButton,
	IconWrapper,
	LabelPlus,
	ListItem,
	Paragraph,
	SegmentedControl,
	Select,
	Sidebar,
	Skeleton,
	TextBubble,
	tokens,
} from '@treams/treams-ui';

import useConversationLearningRecommendations from '../conversations/evaluation-submission/hooks/useConversationLearningRecommendations';
import FeedbackResultQuestion from '../feedback/results/FeedbackResultQuestion';
import { useModalLink } from '../navigation/ModalNavigation';
import NoteCard from '../notes/NoteCard';
import LearningItemCard from '../packages/kakapo-ui-application/pages/learning/components/LearningItemCard';

const Container = styled.div`
	height: 100%;
	overflow: auto;
	display: flex;
	flex-direction: column;
	gap: ${tokens.gap.group}px;
	padding: ${tokens.padding.container}px;
`;

const Header = styled.div`
	display: flex;
	align-items: center;
	gap: ${tokens.gap.group}px;
	width: 100%;
`;

const Title = styled.div`
	display: flex;
	align-items: center;
	gap: ${tokens.gap.element}px;
	flex: 1;
	min-width: 0;
`;

const List = styled.div`
	width: 100%;
	border-top: 1px solid ${tokens.border.subtle};
`;

const SectionBlock = styled.div`
	width: 100%;
	border-bottom: 1px solid ${tokens.border.subtle};
`;

const ROW_GLYPH = 20;

const Row = styled.div`
	display: flex;
	align-items: center;
	width: 100%;
	box-sizing: border-box;
	height: ${tokens.padding.container * 2 + ROW_GLYPH}px;
	padding: 0;
`;

const Toggle = styled.button`
	display: flex;
	align-items: center;
	gap: ${tokens.gap.inline}px;
	flex: 1;
	min-width: 0;
	margin: 0;
	padding: 0;
	border: 0;
	background: transparent;
	cursor: pointer;
	text-align: left;
	font: inherit;
	color: inherit;
`;

const Chevron = styled.span<{ $open?: boolean }>`
	display: flex;
	flex-shrink: 0;
	width: 16px;
	height: 16px;
	color: ${tokens.icon.secondary};
	transform: rotate(${(props) => (props.$open ? '90deg' : '0deg')});
	transition: transform 200ms ease-out;

	svg {
		display: block;
	}
`;

const Glyph = styled.span`
	display: flex;
	flex-shrink: 0;
	width: ${ROW_GLYPH}px;
	height: ${ROW_GLYPH}px;
	color: ${tokens.icon.secondary};

	svg {
		display: block;
	}
`;

const SectionLabel = styled(LabelPlus)`
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const Peek = styled.div`
	padding-bottom: ${tokens.padding.container}px;
`;

function Section(
	props: PropsWithChildren<{
		title: string;
		icon: ReactNode;
		isOpen: boolean;
		onToggle: () => void;
		createLink?: string;
	}>,
) {
	return (
		<SectionBlock>
			<Row>
				<Toggle type="button" onClick={props.onToggle} aria-expanded={props.isOpen}>
					<Chevron $open={props.isOpen}>
						<ChevronRightIcon size={16} />
					</Chevron>
					<Glyph>{props.icon}</Glyph>
					<SectionLabel>{props.title}</SectionLabel>
				</Toggle>
				{props.createLink && (
					<IconButton
						type="plain"
						icon={<AddIcon />}
						link={props.createLink}
						onClick={(event) => event.stopPropagation()}
					/>
				)}
			</Row>
			{props.isOpen && <Peek>{props.children}</Peek>}
		</SectionBlock>
	);
}

function JobPosition(props: { position: graphql.JobPosition }) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const history = useHistory();
	const path = useModalLink(`/job-positions/${props.position.id}`);

	return (
		<ListItem
			small
			title={
				props.position.jobLevel
					? `${localize(props.position.jobTitle.title).text} - ${
							localize(props.position.jobLevel.title).text
						}`
					: localize(props.position.jobTitle.title).text
			}
			meta={`${moment(props.position.startDate).format('MMMM YYYY')} –
        ${
					props.position.endDate
						? moment(props.position.endDate).format('MMMM YYYY')
						: t('conversation.sidebar.jobs.current.title')
				}`}
			onClick={() => history.push(path)}
		/>
	);
}

function SidebarPeekJobs(props: { user: graphql.User }) {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);

	return (
		<Section
			title={t('conversation.sidebar.jobs.title')}
			icon={<SuitcaseIcon size={20} />}
			isOpen={isOpen}
			onToggle={() => setIsOpen(!isOpen)}
		>
			{!props.user.jobPositions && (
				<Skeleton.Repeat count={3}>
					<Sidebar.Item.Skeleton />
				</Skeleton.Repeat>
			)}
			{props.user.jobPositions && props.user.jobPositions.length > 0 && (
				<Card p={0}>
					{props.user.jobPositions.map((position) => (
						<JobPosition key={position.id} position={position} />
					))}
				</Card>
			)}
		</Section>
	);
}

function FeedbackRequestResultsSmall(props: { requestID: string }) {
	const request = graphql.useFeedbackResultsQuery({ variables: { id: props.requestID } });

	const responses = _.sortBy(
		request.data?.feedbackRequest.responses.edges.map(({ node }) => node),
		(response) => `${response.recipient.firstName} ${response.recipient.lastName}`,
	);

	const isSubject = request.data?.feedbackRequest.user.isViewer ?? false;

	return (
		<>
			{!request.data && <FeedbackResultQuestion.Skeleton />}
			{request.data &&
				responses &&
				request.data?.feedbackRequest.questions
					.filter((question) => (question.canSubjectView ?? true) || !isSubject)
					.map((question) => (
						<Box mb={3} key={question.id}>
							<FeedbackResultQuestion
								request={request.data!.feedbackRequest as graphql.FeedbackRequest}
								question={question as graphql.FeedbackRequestQuestion}
								response={undefined}
								responses={responses as graphql.FeedbackResponse[]}
								isSubject={isSubject}
								small
							/>
						</Box>
					))}
		</>
	);
}

export function SidebarPeekConversations(props: {
	userID: string;
	currentConversationID?: string;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const history = useHistory();
	const viewer = useViewer();

	const { data } = graphql.useEvaluationSubmissionsQuery({
		variables: { userID: props.userID, last: 100, isArchived: false },
	});

	const [showAll, setShowAll] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const conversations = _.orderBy(
		data?.user.evaluationSubmissions.edges ?? [],
		({ node }) => node.createdAt,
		'desc',
	)
		.map(({ node }) => node)
		.slice(0, showAll ? data?.user.evaluationSubmissions.edges.length : 4);

	const conversationsPath =
		viewer?.id === props.userID ? `/conversations` : `/users/${props.userID}/conversations`;

	return (
		<Section
			title={t('conversation.sidebar.previous.title')}
			icon={<ConversationIcon size={20} />}
			isOpen={isOpen}
			onToggle={() => setIsOpen(!isOpen)}
		>
			{!conversations && (
				<Skeleton.Repeat count={3}>
					<Sidebar.Item.Skeleton />
				</Skeleton.Repeat>
			)}
			{conversations && conversations.length > 0 && (
				<Card p={0}>
					{conversations.map((conversation) => {
						const date = moment(conversation.deadline).format('L');
						const time = moment(conversation.deadline).format('HH:mm');

						return (
							<ListItem
								key={conversation.id}
								small
								title={localize(conversation.evaluationForm.title).text}
								meta={t('conversation.sidebar.item.dueDateWithTime', { date, time })}
								selected={conversation.id === props.currentConversationID}
								onClick={() => history.push(`${conversationsPath}/${conversation.id}`)}
								trailing={
									<IconWrapper size={16} color={tokens.icon.secondary}>
										<ChevronRightIcon />
									</IconWrapper>
								}
							/>
						);
					})}
				</Card>
			)}
			{!showAll && (data?.user.evaluationSubmissions.edges.length ?? 0) > 4 && (
				<Button
					type="plain"
					title={t('conversation.sidebar.previous.showAll.title')}
					onClick={() => setShowAll(true)}
				/>
			)}
		</Section>
	);
}

function SidebarPeekLearning(props: { conversationID: string }) {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const { available, loading, recommendations } = useConversationLearningRecommendations({
		evaluationSubmissionID: props.conversationID,
		skip: !isOpen,
	});

	if (!available) {
		return null;
	}

	const showSkeletons = loading && recommendations.length === 0;

	return (
		<Section
			title={t('conversation.sidebar.learning.title')}
			icon={<LibraryIcon size={20} />}
			isOpen={isOpen}
			onToggle={() => setIsOpen(!isOpen)}
		>
			{showSkeletons ? (
				<Skeleton.Repeat count={3}>
					<Box mb={2}>
						<LearningItemCard.Skeleton vertical />
					</Box>
				</Skeleton.Repeat>
			) : recommendations.length === 0 ? (
				<Paragraph mb={2}>{t('conversation.sidebar.learning.empty')}</Paragraph>
			) : (
				recommendations.map((recommendation) => (
					<Box
						key={recommendation.learningCourse.id}
						mb={2}
						title={recommendation.reason ?? undefined}
					>
						<LearningItemCard course={recommendation.learningCourse} vertical />
					</Box>
				))
			)}
		</Section>
	);
}

function ComplimentListItem(props: { compliment: Compliment }) {
	return <TextBubble user={props.compliment.sender} text={props.compliment.text} />;
}

function SidebarPeekCompliment(props: { userID: string }) {
	const { t } = useTranslation();
	const userCompliments = graphql.useUserComplimentsQuery({
		variables: { id: props.userID },
	});

	const compliments = userCompliments.data?.user.compliments.edges.slice().reverse() || [];

	const [isOpen, setIsOpen] = useState(false);

	return (
		<Section
			title={t('conversation.sidebar.compliments.title')}
			icon={<HeartIcon size={20} />}
			isOpen={isOpen}
			onToggle={() => setIsOpen(!isOpen)}
		>
			{compliments.length ? (
				<FlexGap flexDirection="column" gap={tokens.gap.element} width="100%">
					{compliments.map(({ node }) => (
						<ComplimentListItem key={node.id} compliment={node as graphql.Compliment} />
					))}
				</FlexGap>
			) : (
				<Paragraph>{t('conversation.sidebar.compliments.empty')}</Paragraph>
			)}
		</Section>
	);
}

function SidebarPeekFeedback(props: { userID: string }) {
	const { t } = useTranslation();
	const feedback = graphql.useFeedbackRequestsQuery({
		variables: { userID: props.userID, last: 10, isArchived: false },
	});

	const options = _.orderBy(
		feedback.data?.user.feedbackRequests.edges ?? [],
		({ node }) => node.createdAt,
		'desc',
	).map(({ node }) => ({
		title: node.title || t('feedback.incoming.card.title'),
		value: node.id,
	}));

	const [isOpen, setIsOpen] = useState(false);
	const [selectedFeedback, setSelectedFeedback] = useState<undefined | string>(undefined);

	useEffect(() => {
		if (!options || options.length < 1) {
			return;
		}

		if (selectedFeedback === undefined) {
			setSelectedFeedback(options[0]!.value);
		}
	}, [options, selectedFeedback]);

	return (
		<Section
			title={t('conversation.sidebar.feedback.title')}
			icon={<DialogueIcon size={20} />}
			isOpen={isOpen}
			onToggle={() => setIsOpen(!isOpen)}
		>
			{options && (
				<Box mb={`${tokens.gap.group}px`}>
					<Select
						options={options}
						selectedValue={selectedFeedback}
						onChange={setSelectedFeedback}
						search={false}
						zIndex={4}
					/>
				</Box>
			)}

			{selectedFeedback && <FeedbackRequestResultsSmall requestID={selectedFeedback} />}
		</Section>
	);
}

function CompactSharedNotesList(props: { userID: string; otherID: string }) {
	const location = useLocation();

	const query = graphql.useMutualNotesQuery({
		variables: {
			first: 5,
			id: props.otherID,
			filters: { showArchived: false },
		},
	});

	useEffect(() => {
		query.refetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location]);

	return (
		<CardList
			translationName={'notes.index'}
			query={query}
			path="user.mutualNotes"
			icon={<DraftsIcon />}
			renderCard={(item: graphql.Note) => (
				<NoteCard userID={props.userID} note={item} compact modal />
			)}
			renderSkeleton={() => <NoteCard.Skeleton compact />}
			compact
			framed={false}
		/>
	);
}

function CompactPrivateNotesList(props: { userID: string }) {
	const location = useLocation();

	const query = graphql.useNotesQuery({
		variables: {
			first: 5,
			filters: {
				isArchived: false,
			},
		},
	});

	useEffect(() => {
		query.refetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location]);

	return (
		<CardList
			translationName={'notes.index'}
			query={query}
			path="notes"
			icon={<DraftsIcon />}
			renderCard={(item: graphql.Note) => (
				<NoteCard userID={props.userID} note={item} compact modal />
			)}
			renderSkeleton={() => <NoteCard.Skeleton compact />}
			compact
			framed={false}
		/>
	);
}

function SidebarPeekNotes(props: { userID: string; otherID: string }) {
	const { t } = useTranslation();

	const [isOpen, setIsOpen] = useState(false);

	const [tab, setTab] = useState<'shared' | 'private'>('shared');

	const link = useModalLink(tab === 'shared' ? `/notes/new/${props.otherID}` : '/notes/new');

	return (
		<Section
			title={t('conversation.sidebar.notes.title')}
			icon={<DraftsIcon size={20} />}
			isOpen={isOpen}
			onToggle={() => setIsOpen(!isOpen)}
			createLink={link}
		>
			<Flex mb={2}>
				<SegmentedControl
					options={[
						{ title: t('conversation.peek.notes.shared'), value: 'shared' },
						{ title: t('conversation.peek.notes.private'), value: 'private' },
					]}
					value={tab}
					onChange={(value) => setTab(value as 'shared' | 'private')}
				/>
			</Flex>

			{tab === 'shared' && <CompactSharedNotesList userID={props.userID} otherID={props.otherID} />}
			{tab === 'private' && <CompactPrivateNotesList userID={props.userID} />}
		</Section>
	);
}

function CompactOneOnOnesList(props: { userID: string; sharedOnly?: boolean }) {
	const location = useLocation();

	const query = graphql.useNotesQuery({
		variables: {
			first: 5,
			filters: {
				isArchived: false,
				subjectID: props.userID,
				collaboratorIDs: props.sharedOnly ? [props.userID] : [],
			},
		},
	});

	useEffect(() => {
		query.refetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location]);

	return (
		<CardList
			translationName={'oneOnOnes.index'}
			query={query}
			path="notes"
			icon={<CalendarIcon />}
			renderCard={(item: graphql.Note) => (
				<NoteCard userID={props.userID} note={item} compact modal />
			)}
			renderSkeleton={() => <NoteCard.Skeleton compact />}
			compact
		/>
	);
}

function SidebarPeekOneOnOnes(props: { userID: string; otherID: string }) {
	const { t } = useTranslation();

	const [isOpen, setIsOpen] = useState(false);
	const [tab, setTab] = useState<'shared' | 'all'>('shared');

	const link = useModalLink(`/notes/new/subject/${props.userID}`);

	return (
		<Section
			title={t('conversation.sidebar.oneOnOnes.title')}
			icon={<FriendsIcon size={20} />}
			isOpen={isOpen}
			onToggle={() => setIsOpen(!isOpen)}
			createLink={link}
		>
			<Flex mb={2}>
				<SegmentedControl
					options={[
						{ title: t('conversation.peek.notes.shared'), value: 'shared' },
						{ title: t('common.all'), value: 'all' },
					]}
					value={tab}
					onChange={(value) => setTab(value as typeof tab)}
				/>
			</Flex>

			<CompactOneOnOnesList userID={props.userID} sharedOnly={tab === 'shared'} />
		</Section>
	);
}

function CompactGoalsList(props: { userID: string; status: graphql.GoalStatus }) {
	const location = useLocation();
	const { t } = useTranslation();

	const goals = graphql.useIndividualGoalsQuery({
		variables: {
			id: props.userID,
			status: props.status,
			last: 10,
		},
	});

	useEffect(() => {
		goals.refetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location]);

	const sortedGoals = _.sortBy(goals.data?.user.goalsV2.edges ?? [], ({ node }) => -node.order).map(
		({ node }) => node,
	);

	return (
		<>
			{!goals.data && (
				<Skeleton.Repeat count={3}>
					<Sidebar.Item.Skeleton />
				</Skeleton.Repeat>
			)}
			{sortedGoals.length > 0 && (
				<Card p={0}>
					{sortedGoals.map((goal) => (
						<SidebarGoalItem key={goal.id} goal={goal as unknown as graphql.Goal} />
					))}
				</Card>
			)}
			{goals.data && sortedGoals.length === 0 && (
				<Paragraph pt={2} pb={4} textAlign="center">
					{t('goals.index.empty.title')}
				</Paragraph>
			)}
		</>
	);
}

function SidebarGoalItem(props: { goal: graphql.Goal }) {
	const { localize } = useLocalize();
	const history = useHistory();
	const path = useModalLink(`/goals/${props.goal.id}`, true);

	return (
		<ListItem small title={localize(props.goal.title).text} onClick={() => history.push(path)} />
	);
}

function SidebarPeekGoals(props: { userID: string; viewer: graphql.User }) {
	const { t } = useTranslation();

	const [isOpen, setIsOpen] = useState(false);

	const link = useModalLink(
		props.viewer.id === props.userID ? '/goals/new' : `/users/${props.userID}/goals/new`,
		true,
	);

	const [tab, setTab] = useState<'active' | 'completed'>('active');

	return (
		<Section
			title={t('conversation.sidebar.goals.title')}
			icon={<TargetIcon size={20} />}
			isOpen={isOpen}
			onToggle={() => setIsOpen(!isOpen)}
			createLink={link}
		>
			<Flex mb={2}>
				<SegmentedControl
					options={[
						{ title: t('conversation.peek.goals.active'), value: 'active' },
						{ title: t('conversation.peek.goals.completed'), value: 'completed' },
					]}
					value={tab}
					onChange={(value) => setTab(value as 'active' | 'completed')}
				/>
			</Flex>

			{tab === 'active' && (
				<CompactGoalsList userID={props.userID} status={graphql.GoalStatus.Active} />
			)}
			{tab === 'completed' && (
				<CompactGoalsList userID={props.userID} status={graphql.GoalStatus.Completed} />
			)}
		</Section>
	);
}

export type ContextSidebarProps = {
	onClose?: () => void;
	user: graphql.User;
	/** Optional: shows the "previous conversations" section with this conversation highlighted */
	currentConversationID?: string;
	/** Optional: ID of the other user for shared notes (defaults to viewer) */
	otherUserID?: string;
	/** Learning recs are only for the employee or manager of this conversation */
	viewerIsConversationParticipant?: boolean;
};

const ContextSidebar = Skeleton(
	function ContextSidebar(props: ContextSidebarProps) {
		const { t } = useTranslation();

		const useFeedback = useFeature(graphql.FeatureName.Feedback);
		const useGoals = useFeature(graphql.FeatureName.Goals);
		const useNotes = useFeature(graphql.FeatureName.Notes);
		const useCompliments = useFeature(graphql.FeatureName.Compliments);
		const useConversations = useFeature(graphql.FeatureName.Evaluations);
		const hasOneOnOnes = useFeature(graphql.FeatureName.OneOnOnes);
		const viewer = useViewer();

		const otherID = props.otherUserID ?? viewer?.id;

		return (
			<Container>
				<Header>
					<Title>
						<Avatar user={props.user} size={24} flexGrow={0} flexShrink={0} />
						<Heading3 lineClamp={1} flex={1} minWidth={0}>
							{t('conversation.sidebar.context.title', {
								name: props.user.firstName,
							})}
						</Heading3>
					</Title>
					<IconButton
						type="secondary"
						icon={<SidebarRightCloseIcon />}
						onClick={() => props.onClose?.()}
					/>
				</Header>
				<List>
					{useConversations && props.user.viewerCanViewEvaluations && (
						<SidebarPeekConversations
							userID={props.user.id}
							currentConversationID={props.currentConversationID}
						/>
					)}
					{props.currentConversationID && props.viewerIsConversationParticipant && (
						<SidebarPeekLearning conversationID={props.currentConversationID} />
					)}
					{useGoals && props.user.viewerCanViewGoals && viewer && (
						<SidebarPeekGoals userID={props.user.id} viewer={viewer} />
					)}
					{useFeedback && props.user.viewerCanViewFeedback && (
						<SidebarPeekFeedback userID={props.user.id} />
					)}
					{useCompliments && props.user.viewerCanViewFeedback && (
						<SidebarPeekCompliment userID={props.user.id} />
					)}
					{useNotes &&
						viewer &&
						otherID &&
						(hasOneOnOnes ? (
							<SidebarPeekOneOnOnes userID={props.user.id} otherID={otherID} />
						) : (
							<SidebarPeekNotes userID={props.user.id} otherID={otherID} />
						))}
					<SidebarPeekJobs user={props.user} />
				</List>
			</Container>
		);
	},
	function ContextSidebarSkeleton() {
		return <Container />;
	},
);

export default ContextSidebar;
